import {
	Link03,
	MarkerPin01,
	Route as RouteIcon,
	User01 as User,
} from "@untitledui/icons";
import type { MouseEvent } from "react";
import { useCallback, useState } from "react";
import { CarIcon } from "@/components/ui/icons/CarIcon";
import { useAddressResolver } from "../../../hooks";
import type { VehicleCardProps } from "../../../types/cards.types";
import { AddressDisplay } from "../address-display";
import { BaseCard } from "../base-card";

export const VehicleCard = ({
	data,
	isSelected = false,
	onClick,
	onRouteClick,
	style,
}: VehicleCardProps) => {
	const isStopped = !data.moving;
	const {
		address: localAddress,
		isLoading: isFetchingAddress,
		fetchIfMissing,
	} = useAddressResolver({
		latitude: data.latitude,
		longitude: data.longitude,
		initialAddress: data.address,
	});
	const [showAddress, setShowAddress] = useState(false);
	const latitude = data.latitude?.replace(",", ".").trim();
	const longitude = data.longitude?.replace(",", ".").trim();
	const hasCoordinates = Boolean(latitude && longitude);

	const handleRouteClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onRouteClick?.({ plate: data.vehiclePlate, vehicleId: data.vehicleId });
	};

	const openMaps = () => {
		if (!hasCoordinates) {
			return;
		}

		window.open(
			`https://www.google.com/maps?q=${latitude},${longitude}`,
			"_blank",
			"noopener,noreferrer"
		);
	};

	const handleOpenMaps = (e: MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		openMaps();
	};

	const handleAddressToggle = useCallback(() => {
		const nextValue = !showAddress;
		setShowAddress(nextValue);
		if (nextValue) {
			fetchIfMissing();
		}
	}, [fetchIfMissing, showAddress]);

	return (
		<BaseCard
			className="w-full cursor-pointer"
			isSelected={isSelected}
			onClick={onClick}
			role="option"
			style={style}
			tabIndex={0}
			variant="vehicle"
		>
			<div className="flex flex-col gap-2">
				<div className="flex items-center justify-between">
					<div className="flex items-center gap-2 rounded-md border border-[#D5D7DA] px-2 py-0.5">
						<div
							className={`h-2 w-2 rounded-full ${
								isStopped
									? "bg-[#F04438] shadow-[#F04438]/50 shadow-lg"
									: "animate-pulse bg-[#17B26A] shadow-[#17B26A]/50 shadow-lg"
							}`}
							title={isStopped ? "Parado" : "Movendo"}
						/>
						<span className="font-normal text-[#414651] text-sm">
							{isStopped ? "Parado" : "Movendo"}
						</span>
					</div>

					{onRouteClick ? (
						<button
							className="rounded-lg border border-[#D5D7DA] p-2"
							onClick={handleRouteClick}
							title="Ver histórico de rotas"
							type="button"
						>
							<RouteIcon className="h-5 w-5 text-[#A4A7AE]" />
						</button>
					) : null}
				</div>

				<div className="flex items-center gap-2">
					<CarIcon className="h-4 w-4 text-[#000000]" />
					<span>{data.vehiclePlate}</span>
					{(data.trailerPlates?.length ?? 0) > 0 ? (
						<span
							className="flex items-center gap-1"
							title="Reboque(s) atribuído(s)"
						>
							<Link03 className="h-3.5 w-3.5 text-brand-500" />
							<span className="text-[#414651]">
								{data.trailerPlates?.join(", ")}
							</span>
						</span>
					) : null}
				</div>

				<AddressDisplay
					address={localAddress}
					className="min-w-0 flex-1"
					isLoading={isFetchingAddress}
					onToggle={handleAddressToggle}
					showToggle={showAddress}
					variant="toggle"
				/>
				<div className="flex items-center gap-2.5">
					<MarkerPin01 className="h-4 w-4 text-gray-500" size={16} />
					{hasCoordinates ? (
						<button
							className="min-w-0 flex-1 truncate text-left text-[14px] text-gray-500 underline"
							onClick={(e) => {
								e.stopPropagation();
								handleOpenMaps(e);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
									e.stopPropagation();
									openMaps();
								}
							}}
							title="Abrir localização no Google Maps"
							type="button"
						>
							Abrir Maps
						</button>
					) : (
						<span className="text-[14px] text-gray-400">
							Abrir Maps indisponível
						</span>
					)}
				</div>

				<div className="flex items-center gap-2">
					<User className="h-4 w-4 text-[#000000]" />
					<span className="text-slate-600 text-sm">{data.driver}</span>
				</div>

				<div className="border-slate-100">
					<p className="text-slate-400 text-xs">
						Última atualização: {data.lastUpdate}
					</p>
				</div>
			</div>
		</BaseCard>
	);
};
